/* START_MODULE_CONTRACT
 * PURPOSE: Общий заголовок модальных форм с overline, Syne-заголовком и подзаголовком.
 * SCOPE: FormDialogHeader, formDialogActionsSx, formDialogContentSx, formDialogFormSx, formDialogPaperSlotProps.
 * DEPENDS: MUI Box, Typography, alpha
 * LINKS: M-FE-APP
 * ROLE: RUNTIME
 * MAP_MODE: EXPORTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * FormDialogHeader - шапка диалога в стиле портфеля проектов.
 * formDialogActionsSx - единые стили футера диалога.
 * formDialogContentSx - прокручиваемая область формы.
 * formDialogFormSx - flex-колонка для form внутри Dialog.
 * formDialogPaperSlotProps - slotProps для Paper диалога.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - Вертикальный скролл: flex-layout для form/content в диалогах.
 * LAST_CHANGE: 2026-07-14 - Общий заголовок для форм задач и сотрудников.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

export const displayFont = '"Syne", "Inter", sans-serif';

export const formDialogActionsSx = {
  flexShrink: 0,
  px: 3,
  py: 2,
  borderTop: 1,
  borderColor: 'divider',
  bgcolor: 'background.default',
};

export const formDialogFormSx = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: 1,
  overflow: 'hidden',
};

export const formDialogContentSx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  pt: 2.5,
};

/* START_METHOD_formDialogPaperSlotProps */
/* START_CONTRACT: formDialogPaperSlotProps
 * PURPOSE: Собрать slotProps Dialog с flex-колонкой, ограничением высоты и transition.
 * INPUTS: { transitionSlotProps: object }.
 * OUTPUTS: { object }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-APP
 * END_CONTRACT: formDialogPaperSlotProps
 */
export function formDialogPaperSlotProps(transitionSlotProps) {
  return {
    transition: transitionSlotProps,
    paper: {
      sx: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100% - 64px)',
        overflow: 'hidden',
      },
    },
  };
}
/* END_METHOD_formDialogPaperSlotProps */

/* START_METHOD_FormDialogHeader */
/* START_CONTRACT: FormDialogHeader
 * PURPOSE: Отобразить градиентную шапку диалога с overline и заголовком.
 * INPUTS: { overline: string, title: string, titleId?: string, subtitle?: string }.
 * OUTPUTS: { JSX.Element }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-APP
 * END_CONTRACT: FormDialogHeader
 */
export default function FormDialogHeader({ overline, title, titleId, subtitle }) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        px: 3,
        pt: 2.5,
        pb: 2,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundImage: (theme) =>
          `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 100%)`,
      }}
    >
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          color: 'primary.main',
          fontWeight: 600,
          letterSpacing: '0.12em',
          lineHeight: 1.4,
          mb: 0.5,
        }}
      >
        {overline}
      </Typography>
      <Typography
        id={titleId}
        variant="h5"
        component="h2"
        sx={{
          fontFamily: displayFont,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}
/* END_METHOD_FormDialogHeader */
